import { observable, action } from 'mobx';
import objectPath from 'object-path';

import { client } from 'core/client';
import { unObserve } from 'core/utils';
import { Processor, Variables } from 'core/query';

import { handleGraphqlErrors, reportRequestError } from './errors';

export const Input = Variables;

export class Mutation {
  updatingResult = false;
  @observable result = null;
  @observable sysError;
  @observable userErrors = null;
  @observable status = 0;
  @observable headers = null;
  processors = observable.map();

  static New(input, options, headers) {
    // TODO: remove this
    const mutation = new this(input, null, options, false, headers);
    mutation.mutate();
    return mutation;
  }

  constructor(
    input,
    extraArgs = null,
    callbacks = {},
    multipleParams = false,
    headers = null,
  ) {
    // Just a list of 'inputs' rather than input and extraArgs?
    this.setCallbacks(callbacks);
    this.input = input;
    this.extraArgs = extraArgs;
    this.multipleParams = multipleParams;
    this.headers = headers;
  }

  async mutate(callbacks) {
    if (this.updatingResult) {
      console.warn(
        `You are calling ${this.constructor.name} recursively. Which is not allowed by Apollo Client/redux`,
      );
      return;
    }
    const context = {};
    if (this.headers) {
      context.headers = this.headers;
    }
    if (callbacks !== undefined) {
      this.setCallbacks(callbacks);
    }
    let variables = {};
    if (this.multipleParams) {
      variables = unObserve(this.input, this.constructor.conf.inputName);
    } else {
      const inputVal =
        this.input instanceof Input ? unObserve(this.input) : this.input;
      variables = Object.assign({
        [this.constructor.conf.inputName]: inputVal,
      });
    }
    if (this.extraArgs) {
      Object.assign(variables, unObserve(this.extraArgs));
    }
    try {
      await client.mutate({
        mutation: this.constructor.conf.GQL,
        variables,
        update: (apolloCache, obj) => {
          this.updatingResult = true;
          this.Update(apolloCache, obj);
          this.updatingResult = false;
        },
        context,
      });
    } catch (e) {
      this.OnError(e);
    }
  }

  setCallbacks({ onSuccess = null, onUserErr = null, onSysErr = null } = {}) {
    this.onSuccess = onSuccess;
    this.onUserErr = onUserErr;
    this.onSysErr = onSysErr;
  }

  addHeaders(headers) {
    this.headers = { ...this.headers, ...headers };
  }

  process(processor, callback, { subPath = null, processorKey = null } = {}) {
    if (processorKey === null) {
      processorKey = this.processors.size;
    }
    this.processors.set(
      processorKey,
      new Processor(this, processor, callback, subPath),
    );
  }

  @action Update = (apolloCache, object) => {
    // when we remove apollo cache normalization
    // should call processor/merge here with new data e.g. LoadData
    this.status = 1;

    // We didn't receive any errors, but we need to check if
    // the result path exists on the result
    const { resultPath } = this.constructor.conf;
    if (resultPath && !objectPath.has(object.data, resultPath)) {
      console.error(
        `Trying to get resultPath '${resultPath}' on Mutation ${this.constructor.name}, which does not exist on the result`,
      );
    }
    this.result = objectPath.get(object.data, resultPath, null);
    if (this.onSuccess) {
      Promise.resolve().then(() => {
        this.onSuccess(this.result, apolloCache);
      });
    }

    this.processors.forEach(processor => {
      processor.run(objectPath.get(object.data, resultPath, null));
    });
  };

  @action OnError = err => {
    const [sysErr, userErrs] = handleGraphqlErrors(this, err);

    this.status = 2;

    // userErrs is an array of errors recognised as expected errors in backend go and flask
    // Everything else passes through to sysErrors and logging.
    if (userErrs.length !== 0) {
      // if there are user errors which should be handled set them
      this.userErrors = userErrs;

      if (this.onUserErr) {
        this.onUserErr(userErrs[0]);
      }
    } else if (sysErr) {
      this.sysError = sysErr;

      if (this.onSysErr) {
        this.onSysErr(sysErr);
      }

      reportRequestError(sysErr, this);
    }
  };
}
